import axios, { AxiosRequestConfig } from "axios";
import { cache } from "react";
import { classifyUrl, logApiError, logApiRequest, logApiResponse } from "./logger";

if (process.env.NEXT_PUBLIC_BACKEND_URL) {
  axios.defaults.baseURL = process.env.NEXT_PUBLIC_BACKEND_URL;
}

axios.interceptors.request.use((config) => {
  const url = config.baseURL && config.url && !config.url.startsWith("http")
    ? config.baseURL.replace(/\/$/, "") + "/" + config.url.replace(/^\//, "")
    : config.url ?? "";
  const kind = classifyUrl(url);
  logApiRequest(kind, config.method ?? "get", url);
  // attach kind so the response interceptor can reuse it
  (config as any).__kind = kind;
  return config;
});

axios.interceptors.response.use(
  (response) => {
    const config = response.config as any;
    const url = response.config.url ?? "";
    const kind = config.__kind ?? classifyUrl(url);
    logApiResponse(kind, response.config.method ?? "get", url, response.status, response.statusText);
    return response;
  },
  (error) => {
    const config = error.config as any;
    const url = error.config?.url ?? "";
    const kind = config?.__kind ?? classifyUrl(url);
    logApiError(
      kind,
      error.config?.method ?? "get",
      url,
      error.response?.status,
      error.response?.statusText ?? error.message
    );
    return Promise.reject(error);
  }
);

// Interceptor-free axios instance used only for token refresh calls.
// Defined here so both fetchIfAuth (proactive) and the response interceptor
// in client.tsx (reactive) can use the same isolated instance.
export const refreshAxios = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BACKEND_URL,
  headers: { Accept: "application/json", "Content-Type": "application/json" },
});

export async function basicFetcher<K = any, C = any>(
  url: string,
  config?: AxiosRequestConfig<C>
) {
  try {
    const { data } = await axios<K>(url, config);
    return data;
  } catch (err: any) {
    if (err?.response?.data) {
      console.log(err?.response?.data, `Error fetching ${url}`);
      throw new Error(err?.response?.data);
    } else if (err?.response) {
      console.log(err?.response, `Error fetching ${url}`);
      throw new Error(err?.response);
    } else {
      console.log(err, `Error fetching ${url}`);
      throw new Error(err);
    }
  }
}

export async function fetchIfAuth<K = any, C = any>(
  url: string,
  config?: AxiosRequestConfig<C>
) {
  if (typeof window === "undefined") throw new Error("Invalid authorization");

  const imprt = window.localStorage.getItem("imprt");
  if (!imprt) throw new Error("Invalid authorization");

  // Proactive refresh: if no access token is in memory (e.g. after a hard
  // page reload where only the refresh token survived), refresh before the
  // protected request so it never hits the server without auth.
  if (!axios.defaults.headers.common["Authorization"]) {
    try {
      const { data } = await refreshAxios.post<{ access: string; refresh: string }>(
        "auth/jwt/refresh/",
        { refresh: imprt }
      );
      localStorage.setItem("imprt", data.refresh);
      localStorage.setItem("uat", data.access);
      axios.defaults.headers.common["Authorization"] = "Bearer " + data.access;
    } catch {
      // Refresh token is also expired/invalid — force a clean login.
      localStorage.removeItem("imprt");
      localStorage.removeItem("uat");
      throw new Error("Invalid authorization");
    }
  }

  return basicFetcher<K, C>(url, config);
}

export const cachedFetcher = cache(basicFetcher);
